import React, { Fragment } from 'react'
import { StyleSheet, View } from 'react-native'
import { color, spacing, fontFamily, fontWeight } from '@partipost-cm-new/app-preview/dist/Styles/Base'
import { moderateScale } from '@partipost-cm-new/app-preview/dist/Utils/Scaling'
import PartiText from '@partipost-cm-new/app-preview/dist/Components/PartiText'
import { useNewTranslation } from 'hooks'
import Icon, { IconName } from 'particles/Icon'
import {
    selectCampaignSchedule,
    selectFollowPlaceholders,
    selectReviewTextImagePlaceholders,
    selectTemplateDetail,
} from 'state/campaignTemplates'
import { useSelector } from 'react-redux'
import { parseBackendDate } from 'services/dates'
import { Country, TASK_TYPE_ENUM } from 'services/api/constants'
import { StepData, StepSchedule, TaskSchedule } from 'services/api/campaignTemplates'
import styled from 'particles/themes/styled'
import { selectCampaignInformation } from 'state/appPreview/selector/campaign'
import { selectCampaignByTemplateData } from 'state'
import PlaceholderComponent from '../common/InterpolationList'
import { ValueObject } from 'state/appPreview'
import { interpolate } from 'components/utils/interpolation'

const TaskSchedulePreview = () => {
    const campaign = useSelector(selectCampaignByTemplateData)
    const campaignSchedule = useSelector(selectCampaignSchedule)
    const templateDetail = useSelector(selectTemplateDetail)
    const campaignInformation = useSelector(selectCampaignInformation)
    const [reviewPlatform] = useSelector(selectReviewTextImagePlaceholders)
    const [followPlatform] = useSelector(selectFollowPlaceholders)

    const [t] = useNewTranslation()
    const registrationEndDisplay =
        campaignSchedule?.registration_time_window?.end && templateDetail?.country
            ? parseBackendDate(campaignSchedule?.registration_time_window?.end, templateDetail.country, {
                  showTime: false,
                  showYear: true,
                  addSeconds: -1,
              })
            : ''

    return (
        <Fragment>
            <View style={{ paddingHorizontal: spacing.medium }}>
                <PartiText style={styles.taskHeader}>{t('appCampaign.campaignTask.taskTitle')}</PartiText>
                {!!campaignInformation.registration_enable && (
                    <PartiText style={styles.taskDesc}>
                        {t('appCampaign.campaignDetails.registrationRequiredSubtext', {
                            registrationEndDate: registrationEndDisplay || 'DD MM',
                        })}
                    </PartiText>
                )}

                {templateDetail?.task_elements?.map(task => {
                    const hasReviewPlatform = task.placeholder_keys.includes('review_platform')
                    const hasFollowPlatform = task.placeholder_keys.includes('follow_platform')
                    const hasBrandName = task.placeholder_keys.includes('brand_name')
                    const taskDescription = task.data.description
                    const interpolateValues: ValueObject[] = []

                    let steps = [...task.data.steps]
                    if (hasBrandName) {
                        interpolateValues.push({
                            brand_name: campaign?.brand.name ?? '',
                            emptyValue: t('manageTeams.teamPage.brandNameTableLabel'),
                            isInline: true,
                        })
                    }
                    if (hasReviewPlatform || hasFollowPlatform) {
                        interpolateValues.push({
                            ...(reviewPlatform
                                ? { review_platform: reviewPlatform ?? '' }
                                : { follow_platform: followPlatform ?? '' }),
                            emptyValue: t('campaignCategory.socialMediaPlatformTitle'),
                            isInline: true,
                        })
                        steps = steps.map((item, index) =>
                            index === 0
                                ? {
                                      ...item,
                                      data: {
                                          ...item.data,
                                          title: item.data.title,
                                      },
                                  }
                                : item,
                        )
                    }

                    const title =
                        hasBrandName && task.data.title
                            ? interpolate(task.data.title, {
                                  brand_name: campaign?.brand.name,
                              })
                            : task.data.title

                    return (
                        <TaskScheduleItem
                            key={task.uuid}
                            title={title}
                            type={task.data.type}
                            subTitle={taskDescription}
                            steps={steps}
                            schedule={campaignSchedule?.tasks.find(
                                taskSchedule => taskSchedule.type === task.data.type,
                            )}
                            country={templateDetail?.country}
                            interpolateValues={interpolateValues}
                        />
                    )
                })}
            </View>
        </Fragment>
    )
}

export default TaskSchedulePreview

interface TaskScheduleItemProps {
    title: string
    subTitle?: string
    type: string
    steps: StepData[]
    schedule?: TaskSchedule
    country: Country
    interpolateValues: ValueObject[]
}

const getStepScheduleIcon = (type: string): IconName => {
    let iconName: IconName = 'calendarDots'
    if (type.includes('submit')) {
        iconName = 'paperPlaneRight'
    } else if (type.includes('post')) {
        iconName = 'imageSquare'
    }

    return iconName
}

const getTaskLabelIcon = (taskType: string): IconName => {
    switch (taskType) {
        case TASK_TYPE_ENUM.PURCHASE_PRODUCT:
            return 'shoppingCart'
        case TASK_TYPE_ENUM.IG_POST:
        case TASK_TYPE_ENUM.IG_STORY:
        case TASK_TYPE_ENUM.IG_STORY_V02:
            return 'instagramLogo'
        case TASK_TYPE_ENUM.FB_POST:
            return 'facebookLogo'
    }
    return 'calendarCheck'
}

const schedulePlaceholder = 'DD MM - DD MM'

const TaskScheduleItem = ({
    title,
    subTitle,
    type,
    steps,
    schedule,
    country,
    interpolateValues,
}: TaskScheduleItemProps) => {
    const [t] = useNewTranslation()

    const getScheduleText = (stepSchedule?: StepSchedule): string => {
        if (!stepSchedule?.time_window.start || !stepSchedule.time_window.end) {
            return schedulePlaceholder
        }
        const start = parseBackendDate(stepSchedule.time_window.start, country, {
            showTime: false,
            showYear: false,
        })
        const end = parseBackendDate(stepSchedule.time_window.end, country, {
            showTime: false,
            showYear: false,
            addSeconds: -1,
        })

        return `${start} - ${end}`
    }

    return (
        <View>
            <View style={styles.flexRowBetween}>
                <View style={styles.flexRow}>
                    <Icon name={getTaskLabelIcon(type as TASK_TYPE_ENUM)} size="24px" />
                    <PartiText style={styles.taskTitle}>{title}</PartiText>
                </View>
                <PartiText style={styles.linkStyle}>
                    {t('button.taskDetails')} {'>'}
                </PartiText>
            </View>
            <PartiText style={styles.taskSubTitle}>
                <PlaceholderComponent template={subTitle ?? ''} values={interpolateValues} />
            </PartiText>

            {steps.map(stepTask => {
                const scheduleText = getScheduleText(
                    schedule?.steps.find(stepSchedule => stepSchedule.type === stepTask.type),
                )
                return (
                    <View key={stepTask.data.title} style={[styles.flexRowBetween, styles.stepContainer]}>
                        <View style={[styles.flexRow, styles.stepTitleContainer]}>
                            <IconStyled name={getStepScheduleIcon(stepTask.type)} form="fill" size="24px" />
                            <PartiText style={{ ...styles.marginLeft, ...styles.stepTitle }}>
                                <PlaceholderComponent template={stepTask.data.title ?? ''} values={interpolateValues} />
                            </PartiText>
                        </View>
                        <PartiText
                            style={scheduleText !== schedulePlaceholder ? styles.dateText : styles.datePlaceholder}
                        >
                            {scheduleText || schedulePlaceholder}
                        </PartiText>
                    </View>
                )
            })}
            <View style={styles.divider} />
        </View>
    )
}

const baseStyles = StyleSheet.create({
    flexRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    title: {
        fontFamily: fontFamily.title,
        fontWeight: fontWeight.bold,
    },
})

const styles = StyleSheet.create({
    taskHeader: {
        ...baseStyles.title,
        fontSize: moderateScale(20),
        paddingVertical: spacing.small,
        color: color.tuna,
    },
    taskDesc: {
        ...baseStyles.title,
        fontSize: moderateScale(14),
        color: '#253778',
        marginBottom: spacing.medium,
    },
    taskTitle: {
        ...baseStyles.title,
        fontSize: moderateScale(16),
        color: 'black',
        marginHorizontal: moderateScale(8),
    },
    taskSubTitle: {
        fontSize: moderateScale(12),
        color: color.doveGray,
        marginBottom: spacing.small,
    },
    flexRowBetween: {
        ...baseStyles.flexRow,
        justifyContent: 'space-between',
    },
    flexRow: {
        ...baseStyles.flexRow,
        flex: 1,
    },
    stepTitleContainer: {
        marginRight: moderateScale(8),
    },
    marginLeft: {
        marginLeft: moderateScale(8),
    },
    linkStyle: {
        textDecorationLine: 'underline',
        textDecorationColor: '#253778',
        color: '#253778',
    },
    stepContainer: {
        marginBottom: spacing.small,
    },
    stepTitle: {
        fontSize: moderateScale(14),
        color: color.tuna,
        fontFamily: fontFamily.copy,
        fontWeight: fontWeight.medium,
    },
    datePlaceholder: {
        fontSize: moderateScale(16),
        color: color.silverChalice,
        fontWeight: fontWeight.medium,
    },
    dateText: {
        fontSize: moderateScale(16),
        fontWeight: fontWeight.medium,
        color: color.tuna,
    },
    divider: {
        borderBottomWidth: 1,
        borderBottomColor: color.alto,
        marginVertical: moderateScale(12),
    },
})

const IconStyled = styled(Icon)`
    flex-shrink: 0;
`
